/**
 * WORLD-CUP-MOBILE-UX-FINAL-P0 — Quick Return tracker.
 *
 * Tracks the user's last meaningful action (prediction, challenge, ranking
 * view) so the UI can offer a 1-tap return entry on the next session.
 * Local-only, no DB, degrades to nothing when empty.
 */
import { useSyncExternalStore } from "react";

const KEY = "vs:mobile_quick_return_v1";
const MAX_AGE_MS = 1000 * 60 * 60 * 36; // 36h

export type QuickReturnKind = "prediction" | "challenge" | "ranking";

export interface QuickReturnEntry {
  kind: QuickReturnKind;
  label: string;
  href: string;
  at: number;
}

function read(): QuickReturnEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QuickReturnEntry;
    if (!parsed?.at || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();
let version = 0;
function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

export function recordQuickReturn(entry: Omit<QuickReturnEntry, "at">) {
  if (typeof window === "undefined") return;
  try {
    const payload: QuickReturnEntry = { ...entry, at: Date.now() };
    window.localStorage.setItem(KEY, JSON.stringify(payload));
    emit();
  } catch {
    /* noop */
  }
}

export function clearQuickReturn() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(KEY);
  emit();
}

/**
 * Bootstrap: passively listens to the global `vs:event` bus and records
 * the last meaningful action so the Quick Return chip can offer 1-tap
 * resume on the next session.
 */
export function initQuickReturnListener() {
  if (typeof window === "undefined") return () => undefined;
  const onEvt = (e: Event) => {
    const evt = e as CustomEvent<{ type?: string; payload?: Record<string, unknown> }>;
    const type = evt.detail?.type;
    if (!type) return;
    if (type === "first_prediction_completed" || type === "prediction_submitted") {
      recordQuickReturn({
        kind: "prediction",
        label: "Volver a tu palpite",
        href: "/",
      });
    } else if (type === "challenge_accepted" || type === "viral_challenge_clicked") {
      recordQuickReturn({
        kind: "challenge",
        label: "Continuar el reto",
        href: "/social",
      });
    } else if (type === "state_team_selected" || type === "nation_selected") {
      recordQuickReturn({
        kind: "ranking",
        label: "Ver tu ranking",
        href: "/nation-wars",
      });
    }
  };
  window.addEventListener("vs:event", onEvt as EventListener);
  return () => window.removeEventListener("vs:event", onEvt as EventListener);
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const getSnap = () => version;

export function useQuickReturn(): QuickReturnEntry | null {
  useSyncExternalStore(subscribe, getSnap, getSnap);
  return read();
}